import { createModel } from '@rematch/core';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import {
  IPreHeatRecipeEditable,
  IRecipe,
  IRecipeEditable,
  IRecipeStep,
  IRecipeStepEditable,
  IReorderedRecipeStep,
} from '../../types/recipes-types';
import { AxiosError } from 'axios';
import { generatePath } from 'react-router-dom';
import CycleSettings from '../../enums/cycle-setting.enum';

const DEFAULT_STATE: {
  recipes: IRecipe[];
  recipe: IRecipe | null;
  steps: IRecipeStep[];
  step: IRecipeStep | null;
} = {
  recipes: [],
  recipe: null,
  steps: [],
  step: null,
};

export const recipes = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        recipe: DEFAULT_STATE.recipe,
      };
    },
    resetStep(state) {
      return {
        ...state,
        step: DEFAULT_STATE.step,
      };
    },
    resetState() {
      return { ...DEFAULT_STATE };
    },
  },
  effects: (dispatch) => {
    return {
      async createRecipe(payload: IRecipeEditable | IPreHeatRecipeEditable) {
        try {
          const response = await http.post(URL.RECIPES, payload);
          toast.success('Recipe created successfully');
          return response.data;
        } catch (error: any) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },
      async createRecipeFromScratch(payload: IRecipeEditable & {steps: IRecipeStepEditable[]}) {
        try {
          await http.post(URL.RECIPE_FROM_SCRATCH, payload);
          toast.success('Recipe created successfully');
        } catch (error: any) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },

      async createPreHeatRecipeFromScratch(payload: IPreHeatRecipeEditable) {
        try {
          await http.post(URL.RECIPES, payload);
          toast.success('Recipe created successfully');
        } catch (error: any) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },

      async getRecipes(params: { mode?: CycleSettings } = {}): Promise<IRecipe[]> {
        try {
          const response = await http.get(URL.RECIPES, { params });

          dispatch.recipes.set({
            recipes: response.data,
          });

          return response.data;
        } catch (error: any) {
          toast.error(error.message);
          return [];
        }
      },

      async getRecipe(id: string): Promise<IRecipe> {
        try {
          const response = await http.get(generatePath(URL.RECIPE, { id }));

          dispatch.recipes.set({
            recipe: response.data,
          });

          return response.data
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getRecipeSteps(id: string): Promise<IRecipeStep[]> {
        try {
          const response = await http.get(generatePath(URL.RECIPE_STEPS, { id }));

          dispatch.recipes.set({
            steps: response.data,
          });

          return response.data
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async removeRecipe(id: string) {
        try {
          await http.delete(generatePath(URL.RECIPE, { id }));
          toast.success('Recipe deleted successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async updateRecipe(payload: { id: string; body: Partial<IRecipe> }) {
        try {
          const response = await http.put(generatePath(URL.RECIPE, { id: payload.id }), payload.body);

          dispatch.recipes.set({ recipe: response.data });
          toast.success('Recipe updated successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async createStep(payload: { id: string; data: IRecipeStepEditable }) {
        try {
          await http.post(generatePath(URL.RECIPE_STEPS, { id: payload.id }), payload.data);
          toast.success('Recipe step created successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getStep(payload: { recipeid: string; stepId: string }) {
        try {
          const response = await http.get(
            generatePath(URL.RECIPE_STEP, {
              id: payload.recipeid,
              step_id: payload.stepId,
            }),
          );

          if (response.status === 200) {
            dispatch.recipes.set({
              step: response.data,
            });
          }
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async updateStep(payload: { recipeid: string; stepId: string; body: IRecipeStepEditable }) {
        try {
          await http.put(
            generatePath(URL.RECIPE_STEP, {
              id: payload.recipeid,
              step_id: payload.stepId,
            }),
            payload.body,
          );
          toast.success('Recipe step updated successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },
      async removeStep(payload: { recipeid: string; stepId: string }) {
        try {
          await http.delete(
            generatePath(URL.RECIPE_STEP, {
              id: payload.recipeid,
              step_id: payload.stepId,
            }),
          );
          toast.success('Recipe step deleted successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async reorderSteps(payload: { recipeid: string; steps: IReorderedRecipeStep[] }) {
        try {
          await http.put(
            generatePath(URL.RECIPE_STEPS_REORDER, {
              id: payload.recipeid,
            }),
            { data: payload.steps },
          );
          toast.success('Recipe steps reordered successfully');
        } catch (error: any) {
          toast.error(error.message);
        }
      },
    };
  },
});
